import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

// Piecewise distributor (AM) dashboard hooks. Each one drives a single
// section so the page can paint as soon as its slice resolves instead of
// gating everything on a single ~22-query waterfall. The combined endpoint
// is still available via apiClient.getDistributorAnalytics() — those
// callers (e.g. server-side stitching, the supervisor pull) are unaffected.

type DateRange = { startDate?: Date; endDate?: Date } | undefined;
type Intervals = { revenueInterval?: string } | undefined;

// Date ranges are Date objects so we can't use them as query keys directly —
// serialize to ISO strings (or null) for stable, comparable keys.
const dateRangeKey = (dateRange: DateRange) => ({
  start: dateRange?.startDate?.toISOString() ?? null,
  end: dateRange?.endDate?.toISOString() ?? null,
});

export function useDistributorHeader(distributorId: string | undefined, dateRange: DateRange) {
  return useQuery({
    queryKey: ['distributor-analytics', 'header', distributorId ?? 'me', dateRangeKey(dateRange)],
    queryFn: async () => {
      const response = await apiClient.getDistributorAnalyticsHeader(distributorId, dateRange);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load distributor header analytics');
      }
      return response.data;
    },
  });
}

export function useDistributorTrends(
  distributorId: string | undefined,
  dateRange: DateRange,
  intervals: Intervals
) {
  return useQuery({
    queryKey: [
      'distributor-analytics',
      'trends',
      distributorId ?? 'me',
      dateRangeKey(dateRange),
      intervals?.revenueInterval ?? 'monthly',
    ],
    queryFn: async () => {
      const response = await apiClient.getDistributorAnalyticsTrends(distributorId, dateRange, intervals);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load distributor trends analytics');
      }
      return response.data;
    },
  });
}

export function useDistributorRankings(distributorId: string | undefined, dateRange: DateRange) {
  return useQuery({
    queryKey: ['distributor-analytics', 'rankings', distributorId ?? 'me', dateRangeKey(dateRange)],
    queryFn: async () => {
      const response = await apiClient.getDistributorAnalyticsRankings(distributorId, dateRange);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load distributor rankings analytics');
      }
      return response.data;
    },
    // Rankings is the slow slice (getTopPhysicians fans out into multiple
    // $lookups). Cache it longer than the default and don't refetch on
    // remount so navigating back to the dashboard is instant.
    staleTime: 5 * 60_000,
    refetchOnMount: false,
  });
}
